import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { publishAllChanges, fetchUnpublishedChangesSummary, republishCurrentInventory } from '@/services/publishService'
import { useProductSettingsStore } from '@/stores/productSettings'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { fetchDraftSummary } from '@/services/inventory'
import { fetchIsePrimarySources, fetchIsePrimarySourcesDraftSummary } from '@/services/iseSettingsService'

export const usePublishStore = defineStore('publish', () => {
  const productSettings = useProductSettingsStore()
  const accountSettings = useAccountSettingsStore()
  // Legacy local flag (kept for compatibility); prefer persisted unpublished
  const localDirty = ref(false)
  // Track draft vehicles count
  const draftVehiclesCount = ref(0)
  const hasPrimarySourcesDraft = ref(false)
  const hasGroupSiteConfigDraft = ref(false)
  // When a publish has been initiated successfully, we want the UI to immediately
  // stop showing "Unpublished Changes" without waiting for a full refresh.
  //
  // We *do not* clear the persisted unpublished marker yet, because we still need
  // it for reconcilePublished() and for capturing a published snapshot once ISE
  // processing completes.
  const pendingPublishLastSavedAt = ref<string | null>(null)
  const hasPendingPublish = computed(() => !!pendingPublishLastSavedAt.value)
  const pendingRepublishStartedAt = ref<string | null>(null)
  const hasPendingRepublish = computed(() => !!pendingRepublishStartedAt.value)
  const lastPublishError = ref<{
    status?: number
    error?: string
    code?: string
    message?: string
    messages?: string[]
    details?: any
  } | null>(null)
  const lastRepublishError = ref<{
    status?: number
    error?: string
    code?: string
    message?: string
    messages?: string[]
    details?: any
  } | null>(null)

  function hasMeaningfulUnpublishedSources(u: any): boolean {
    const sources = u?.sources && typeof u.sources === 'object' ? (u.sources as any) : null
    if (!sources) return false

    for (const key of Object.keys(sources)) {
      const entry = sources[key]
      const details = entry?.details
      if (!details || typeof details !== 'object') continue

      if (key === 'fieldMapping') {
        const n = Number((details as any).totalModified ?? (details as any).fieldsChanged?.length ?? 0)
        if (n > 0) return true
        continue
      }

      if (key === 'segments') {
        const added = Array.isArray((details as any).added) ? (details as any).added.length : 0
        const modified = Array.isArray((details as any).modified) ? (details as any).modified.length : 0
        const removed = Array.isArray((details as any).removed) ? (details as any).removed.length : 0
        if (added + modified + removed > 0) return true
        continue
      }

      if (key === 'listingRules') {
        const hasTotals =
          Number((details as any).totalRulesAdded ?? 0) > 0 || Number((details as any).totalRulesRemoved ?? 0) > 0
        const hasSegments =
          (details as any).segments &&
          typeof (details as any).segments === 'object' &&
          Object.keys((details as any).segments).length > 0
        const hasChanges = (details as any).hasChanges === true
        if (hasChanges || hasTotals || hasSegments) return true
        continue
      }

      if (key === 'inventory') {
        const count = Number((details as any).count ?? 0)
        const vehicles = Array.isArray((details as any).vehicles) ? (details as any).vehicles.length : 0
        if (count > 0 || vehicles > 0) return true
        continue
      }

      // Generic fallback: non-empty details object means "something changed"
      if (Object.keys(details).length > 0) return true
    }

    return false
  }

  // Dirty state is now derived from server-truth (drafts exist).
  // No more persisted JAMS unpublished marker.
  const isDirty = computed(() => {
    // If we just initiated a publish, treat as "clean" temporarily until server-truth refreshes.
    if (pendingPublishLastSavedAt.value) return false
    
    // Otherwise, use local dirty flag (set by markDirty calls or draft saves).
    return localDirty.value
  })

  // Check if there are draft vehicles pending publish
  const hasDrafts = computed(() => draftVehiclesCount.value > 0)

  // Combined check: has changes OR has drafts
  const hasAnythingToPublish = computed(() => {
    if (pendingPublishLastSavedAt.value) return false
    return isDirty.value || hasDrafts.value
  })

  // Refresh unpublished changes summary (server-truth).
  // Note: despite the name, this includes vehicle drafts, inventory drafts, and config drafts.
  async function refreshUnpublishedChangesSummary() {
    if (pendingPublishLastSavedAt.value) return

    const endpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const jazelId = accountSettings.accountId
    const sessionToken = accountSettings.sessionToken
    if (!endpoint || !jazelId || !sessionToken) {
      // When we can't resolve required inputs, fail closed (no drafts).
      // This prevents the nav item from sticking around across logouts/account switches.
      localDirty.value = false
      draftVehiclesCount.value = 0
      hasPrimarySourcesDraft.value = false
      hasGroupSiteConfigDraft.value = false
      return
    } else {
      console.log('refreshUnpublishedChangesSummary', endpoint, jazelId)
    }

    const prev = draftVehiclesCount.value
    const prevLocalDirty = localDirty.value
    const prevPrimarySourcesDraft = hasPrimarySourcesDraft.value

    // Use the new authoritative backend endpoint (single call replaces brittle 3-call logic)
    try {
      const summary = await fetchUnpublishedChangesSummary(endpoint, jazelId, sessionToken)
      
      // Set draft counts from server-truth
      const vehicleDraftCount = Number(summary.counts?.vehicleDrafts ?? 0)
      const inventoryDraftCount = Number(summary.counts?.inventoryDrafts ?? 0)
      const hasAnyConfigDraft = Object.values(summary.configDrafts || {}).some((c: any) => c?.exists === true)
      
      // Match current UI aggregation: add 1 for "any config draft exists"
      const combined = vehicleDraftCount + inventoryDraftCount + (hasAnyConfigDraft ? 1 : 0)
      draftVehiclesCount.value = combined
      localDirty.value = combined > 0

      // Set primary sources draft flag from iseSettings config draft
      hasPrimarySourcesDraft.value = summary.configDrafts?.iseSettings?.exists === true
      // Set group site config draft flag
      hasGroupSiteConfigDraft.value = (summary.configDrafts as Record<string, { exists?: boolean }>)?.groupSiteConfig?.exists === true
    } catch (e) {
      console.warn('⚠️ Failed to load unpublished changes summary (keeping previous state):', e)
      // Fail open: keep previous values to avoid flickering nav indicator on transient errors
      draftVehiclesCount.value = prev
      localDirty.value = prevLocalDirty
      hasPrimarySourcesDraft.value = prevPrimarySourcesDraft
    }
  }

  async function markDirty(
    source: 'segments' | 'fieldMapping' | 'inventoryMembership' | 'inventory',
    details?: any,
  ) {
    // Mark as dirty locally (server-truth will be derived from ConfigDraft/VehicleDraft existence).
    // Any new change after a successful publish initiation should re-surface as dirty.
    pendingPublishLastSavedAt.value = null
    localDirty.value = true
    // NOTE: No longer persist to JAMS. Dirty state is derived from server-truth (drafts).
    console.log('📝 markDirty called with:', { source, details })

    // Demote any in-memory Publish Preview status — the ConfigDraft state has
    // moved on, so the previous preview no longer represents current changes.
    // We import lazily to avoid a Pinia circular-init when this store is
    // pulled in before the preview store is registered.
    try {
      const { usePublishPreviewStore } = await import('@/stores/usePublishPreviewStore')
      const previewStore = usePublishPreviewStore()
      previewStore.notifyDraftChanged()
    } catch (error) {
      console.warn('publishPreview.notifyDraftChangedFailed', error)
    }
  }
  
  async function reset() {
    localDirty.value = false
    pendingPublishLastSavedAt.value = null
    pendingRepublishStartedAt.value = null
    lastRepublishError.value = null
    // NOTE: No longer persist to JAMS. Dirty state is derived from server-truth (drafts).
  }

  function clearPendingPublish() {
    pendingPublishLastSavedAt.value = null
  }

  function clearPendingRepublish() {
    pendingRepublishStartedAt.value = null
  }

  // Reconcile after processing completes.
  // In draft-first model, this just clears the pendingPublish in-memory flag.
  async function reconcilePublished(processedAtIso: string, captureSnapshot = false) {
    try {
      console.log('🔄 Reconciling publish state:', {
        processedAt: processedAtIso,
        captureSnapshot,
      })
      
      // Clear pending publish flag (UI will re-check draft counts from server).
      pendingPublishLastSavedAt.value = null
      localDirty.value = false

      // Only capture snapshot if explicitly requested (e.g., after manual publish)
      if (captureSnapshot) {
        console.log('📸 Capturing published snapshot after successful publish')
        await capturePublishedSnapshot(processedAtIso)
      }

      console.log('✅ Reconcile complete')
    } catch (_e) {
      console.error('❌ Error reconciling published state:', _e)
    }
  }

  // Capture current configs as the published snapshot
  async function capturePublishedSnapshot(publishedAt: string) {
    try {
      // Dynamically import to avoid circular dependencies
      const { useVehicleFieldMappingStore } = await import('@/stores/vehicleFieldMapping')
      const { useSegmentStore } = await import('@/stores/segment')
      const { useListingConfigStore, DEFAULT_LISTING_RULES } = await import(
        '@/stores/listingConfig'
      )

      const fieldMappingStore = useVehicleFieldMappingStore()
      const segmentStore = useSegmentStore()
      const listingStore = useListingConfigStore()

      // Capture snapshots of current state (which is now the published state)
      // Field Mapping: originalConfig from JAMS has the correct format (segments under mapping_config)
      const fieldMappingSnapshot = fieldMappingStore.originalConfig
        ? JSON.parse(JSON.stringify(fieldMappingStore.originalConfig))
        : null

      // Segments: Convert from UI array format back to config object format
      let segmentsSnapshot = null
      if (segmentStore.segments && segmentStore.segments.length > 0) {
        segmentsSnapshot = segmentStore.segments.reduce((acc: any, seg: any) => {
          acc[seg.id] = {
            name: seg.name,
            priority: seg.priority,
            custom: seg.custom,
            ruleset: {
              logic: 'AND',
              rules: (seg.criteria || [])
                .filter((c: any) => c.field && c.operator) // Filter out incomplete rules
                .map((c: any) => ({
                  type: 'rule',
                  field: c.field,
                  operator: c.operator,
                  value: c.value,
                })),
            },
          }
          return acc
        }, {})
      } else {
        // Use minimal fallback if none exist
        segmentsSnapshot = {
          all: {
            name: 'All Vehicles',
            exclusive: false,
            priority: 1000,
            custom: false,
            ruleset: { logic: 'AND', rules: [] }
          }
        }
      }

      // Publish Rules: Use the config from the store
      const listingRulesSnapshot = listingStore.listingConfig
        ? JSON.parse(JSON.stringify(listingStore.listingConfig))
        : JSON.parse(JSON.stringify(DEFAULT_LISTING_RULES))

      // Keep snapshot local-only (no JAMS persistence).
      productSettings.publishedSnapshot = {
        lastPublishedAt: publishedAt,
        fieldMapping: fieldMappingSnapshot,
        segments: segmentsSnapshot,
        listingRules: listingRulesSnapshot,
      }

      console.log('📸 Captured published snapshot after successful publish (local-only)')
    } catch (error) {
      console.error('Error capturing published snapshot:', error)
    }
  }

  // Check if changes have been published by comparing with latest ISE processing time
  async function checkAndReconcile(
    feedsEndpoint: string,
    accountId: string,
    captureSnapshot = false,
  ) {
    try {
      // Dynamically import to avoid circular dependency
      const { getSourcesProcessDetails } = await import('@/services/feedSourceService')

      const details = await getSourcesProcessDetails(feedsEndpoint, accountId)
      if (Array.isArray(details) && details.length > 0) {
        let latest = ''
        for (const d of details) {
          if (d?.lastProcessedTime && d.lastProcessedTime > latest) latest = d.lastProcessedTime
        }
        if (latest) {
          await reconcilePublished(latest, captureSnapshot)
        }
      }
    } catch (error) {
      console.error('Error checking reconcile state:', error)
    }
  }

  const publishChanges = async (endpoint: string, accountId: string) => {
    lastPublishError.value = null
    const result = await publishAllChanges(endpoint, accountId, accountSettings.sessionToken)
    if (result.ok) {
      // Immediately reflect "clean" UI state without waiting for a product-settings refresh.
      // In draft-first model, dirty state is derived from server-truth (drafts exist).
      pendingPublishLastSavedAt.value = new Date().toISOString()
      localDirty.value = false
      // Reset draft count immediately after publish (will be updated after processing completes)
      draftVehiclesCount.value = 0
      hasPrimarySourcesDraft.value = false
      hasGroupSiteConfigDraft.value = false
    } else {
      pendingPublishLastSavedAt.value = null
      lastPublishError.value = result
    }
    return result.ok
  }

  const republishCurrentInventoryAction = async (endpoint: string, accountId: string, reason: string) => {
    lastRepublishError.value = null
    const result = await republishCurrentInventory(endpoint, accountId, accountSettings.sessionToken, reason)
    if (result.ok) {
      pendingRepublishStartedAt.value = new Date().toISOString()
    } else {
      pendingRepublishStartedAt.value = null
      lastRepublishError.value = result
    }
    return result.ok
  }
  return {
    isDirty,
    hasDrafts,
    hasAnythingToPublish,
    hasPendingPublish,
    hasPendingRepublish,
    lastPublishError,
    lastRepublishError,
    hasPrimarySourcesDraft,
    hasGroupSiteConfigDraft,
    refreshUnpublishedChangesSummary,
    markDirty,
    reset,
    clearPendingPublish,
    clearPendingRepublish,
    reconcilePublished,
    checkAndReconcile,
    publishChanges,
    republishCurrentInventory: republishCurrentInventoryAction,
  }
})
